import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { cn } from '@/lib/utils'

/**
 * Renders arbitrary email HTML into a sandboxed iframe so the email's
 * own styles never bleed into the CRM UI — and vice versa.
 *
 * Two flavours:
 *   1. Default (scale="fit")          → iframe fills the container, useful
 *      for the live preview pane.
 *   2. Scaled thumbnail (scale=number) → iframe is rendered at its natural
 *      email width (600px) and visually shrunk via CSS transform so every
 *      thumbnail looks exactly like the real email, just smaller.
 */
interface PreviewFrameProps {
    /** Body HTML produced by `renderEmailDocument()` */
    html: string
    /** Subject shown in the faux inbox header (optional) */
    subject?: string
    /** "From" label shown in the faux inbox header */
    fromName?: string
    /** "From" email address shown in the faux inbox header */
    fromEmail?: string
    /** Preheader text shown right under the subject (optional) */
    preheader?: string
    /**
     * - 'fit'        → iframe grows to fill container, great for the preview pane.
     * - 'fit-width'  → iframe is laid out at the natural email width and scaled
     *                  via CSS transform to exactly fit the container width.
     *                  The outer height is clamped by `maxHeight` so the
     *                  preview shows the "top slice" of a long email.
     * - number       → fixed scale applied via CSS transform; iframe is laid
     *                  out at the natural email width (EMAIL_WIDTH) and cropped
     *                  with overflow:hidden. Best for known-sized thumbnails.
     */
    scale?: 'fit' | 'fit-width' | number
    /** Optional max-height for content (scroll) when scale === 'fit' or 'fit-width' */
    maxHeight?: number | string
    /** Show the faux inbox header (default true) */
    showHeader?: boolean
    /** Background color around the email card */
    pageBg?: string
    className?: string
    /** Passed through to iframe title for a11y */
    title?: string
}

const EMAIL_WIDTH = 600

function buildDocument(opts: {
    html: string
    subject?: string
    preheader?: string
    fromName?: string
    fromEmail?: string
    showHeader: boolean
    pageBg: string
}): string {
    const {
        html,
        subject,
        preheader,
        fromName,
        fromEmail,
        showHeader,
        pageBg,
    } = opts

    const headerHtml = showHeader
        ? `
        <div class="tgd-inbox-header">
            <div class="tgd-inbox-row">
                <div class="tgd-avatar">${(fromName || 'T')
                    .charAt(0)
                    .toUpperCase()}</div>
                <div class="tgd-from">
                    <div class="tgd-from-name">${escape(fromName || 'Your store')}</div>
                    <div class="tgd-from-email">${escape(fromEmail || 'noreply@yourstore.com')}</div>
                </div>
            </div>
            ${subject ? `<div class="tgd-subject">${escape(subject)}</div>` : ''}
            ${preheader ? `<div class="tgd-preheader">${escape(preheader)}</div>` : ''}
        </div>`
        : ''

    return `<!doctype html>
<html>
    <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=${EMAIL_WIDTH}, initial-scale=1" />
        <style>
            html, body {
                margin: 0;
                padding: 0;
                background: ${pageBg};
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
                    "Helvetica Neue", Arial, sans-serif;
                color: #111827;
                -webkit-font-smoothing: antialiased;
            }
            body {
                width: ${EMAIL_WIDTH}px;
                min-height: 100%;
            }
            .tgd-inbox-header {
                background: #ffffff;
                border-bottom: 1px solid #e5e7eb;
                padding: 14px 20px;
            }
            .tgd-inbox-row {
                display: flex;
                align-items: center;
                gap: 10px;
            }
            .tgd-avatar {
                width: 32px;
                height: 32px;
                border-radius: 999px;
                background: linear-gradient(135deg, #6366f1, #8b5cf6);
                color: #fff;
                font-weight: 600;
                font-size: 13px;
                display: flex;
                align-items: center;
                justify-content: center;
            }
            .tgd-from-name {
                font-size: 13px;
                font-weight: 600;
                color: #111827;
                line-height: 1.2;
            }
            .tgd-from-email {
                font-size: 11px;
                color: #6b7280;
                line-height: 1.2;
            }
            .tgd-subject {
                margin-top: 10px;
                font-size: 15px;
                font-weight: 600;
                color: #111827;
                line-height: 1.3;
            }
            .tgd-preheader {
                margin-top: 2px;
                font-size: 12px;
                color: #6b7280;
                line-height: 1.3;
            }
            .tgd-body-wrapper {
                padding: 20px;
            }
            .tgd-email-card {
                background: #ffffff;
                border-radius: 12px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 14px rgba(0,0,0,0.05);
                padding: 28px 28px 32px 28px;
                font-size: 15px;
                line-height: 1.6;
                color: #374151;
            }
            .tgd-email-card p,
            .tgd-email-card h1,
            .tgd-email-card h2,
            .tgd-email-card h3 { margin: 0 0 12px 0; }
            .tgd-email-card a { color: #2563eb; }
            .tgd-email-card img { max-width: 100%; height: auto; }
            .tgd-empty {
                color: #9ca3af;
                font-style: italic;
                text-align: center;
                padding: 40px 0;
            }
        </style>
    </head>
    <body>
        ${headerHtml}
        <div class="tgd-body-wrapper">
            <div class="tgd-email-card">
                ${html || '<div class="tgd-empty">Your email body will appear here</div>'}
            </div>
        </div>
    </body>
</html>`
}

function escape(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

export function PreviewFrame(props: PreviewFrameProps) {
    const {
        html,
        subject,
        preheader,
        fromName,
        fromEmail,
        scale = 'fit',
        maxHeight,
        showHeader = true,
        pageBg = '#f3f4f6',
        className,
        title = 'Email preview',
    } = props

    const doc = buildDocument({
        html,
        subject,
        preheader,
        fromName,
        fromEmail,
        showHeader,
        pageBg,
    })

    if (scale === 'fit') {
        return (
            <iframe
                title={title}
                sandbox="allow-same-origin"
                srcDoc={doc}
                className={cn('h-full w-full border-0', className)}
                style={{
                    maxHeight:
                        typeof maxHeight === 'number'
                            ? `${maxHeight}px`
                            : maxHeight,
                }}
            />
        )
    }

    if (scale === 'fit-width') {
        return (
            <FitWidthPreviewFrame
                doc={doc}
                className={className}
                title={title}
                maxHeight={maxHeight}
            />
        )
    }

    // Scaled thumbnail: iframe is laid out at natural email width, then
    // visually shrunk to fit its container. We also measure the real content
    // height so the wrapper doesn't leave huge empty space below short emails.
    return <ScaledPreviewFrame doc={doc} scale={scale} className={className} title={title} />
}

/**
 * Fit-width variant — auto-measures its own container width and computes the
 * scale on the fly so every thumbnail looks identical to the real email at
 * exactly the card's width, regardless of grid size.
 */
function FitWidthPreviewFrame(props: {
    doc: string
    className?: string
    title: string
    maxHeight?: number | string
}) {
    const { doc, className, title, maxHeight } = props
    const containerRef = useRef<HTMLDivElement | null>(null)
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    const [containerWidth, setContainerWidth] = useState<number>(EMAIL_WIDTH)
    const [contentHeight, setContentHeight] = useState<number>(EMAIL_WIDTH)

    // Track container width responsively.
    useLayoutEffect(() => {
        const el = containerRef.current
        if (!el) return
        const update = () => {
            const w = el.getBoundingClientRect().width
            if (w > 0) setContainerWidth(w)
        }
        update()
        const ro = new ResizeObserver(update)
        ro.observe(el)
        return () => ro.disconnect()
    }, [])

    // Measure iframe content height so we never show more empty space than
    // needed (short emails stay short).
    const measure = () => {
        const iframe = iframeRef.current
        if (!iframe) return
        try {
            const body = iframe.contentDocument?.body
            const docEl = iframe.contentDocument?.documentElement
            if (!body || !docEl) return
            const h = Math.max(
                body.scrollHeight,
                docEl.scrollHeight,
                body.offsetHeight,
                docEl.offsetHeight,
            )
            if (h > 0) setContentHeight(h)
        } catch {
            // defensive
        }
    }

    useLayoutEffect(() => {
        measure()
        const t1 = setTimeout(measure, 50)
        const t2 = setTimeout(measure, 300)
        return () => {
            clearTimeout(t1)
            clearTimeout(t2)
        }
    }, [doc])

    useEffect(() => {
        const iframe = iframeRef.current
        if (!iframe) return
        const onLoad = () => measure()
        iframe.addEventListener('load', onLoad)
        return () => iframe.removeEventListener('load', onLoad)
    }, [])

    const scale = containerWidth / EMAIL_WIDTH
    const scaledHeight = contentHeight * scale
    const maxH =
        typeof maxHeight === 'number'
            ? maxHeight
            : typeof maxHeight === 'string'
              ? Number.parseInt(maxHeight, 10) || undefined
              : undefined
    const wrapperHeight = maxH
        ? Math.min(scaledHeight, maxH)
        : scaledHeight

    return (
        <div
            ref={containerRef}
            className={cn('relative w-full overflow-hidden', className)}
            style={{ height: `${wrapperHeight}px` }}
        >
            <iframe
                ref={iframeRef}
                title={title}
                sandbox="allow-same-origin"
                srcDoc={doc}
                style={{
                    width: `${EMAIL_WIDTH}px`,
                    height: `${contentHeight}px`,
                    border: 0,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                }}
            />
        </div>
    )
}

function ScaledPreviewFrame(props: {
    doc: string
    scale: number
    className?: string
    title: string
}) {
    const { doc, scale, className, title } = props
    const iframeRef = useRef<HTMLIFrameElement | null>(null)
    const [contentHeight, setContentHeight] = useState<number>(600)

    // Measure the iframe content height after load + on resize to size the
    // outer wrapper proportionally (scaled). Avoids tall empty whitespace.
    const measure = () => {
        const iframe = iframeRef.current
        if (!iframe) return
        try {
            const body = iframe.contentDocument?.body
            const docEl = iframe.contentDocument?.documentElement
            if (!body || !docEl) return
            const h = Math.max(
                body.scrollHeight,
                docEl.scrollHeight,
                body.offsetHeight,
                docEl.offsetHeight,
            )
            if (h > 0) setContentHeight(h)
        } catch {
            // Same-origin srcDoc should always be readable, but be defensive.
        }
    }

    useLayoutEffect(() => {
        measure()
        // Two more frames catch late font / image layout
        const t1 = setTimeout(measure, 50)
        const t2 = setTimeout(measure, 300)
        return () => {
            clearTimeout(t1)
            clearTimeout(t2)
        }
        // Re-run when the document content changes
    }, [doc])

    useEffect(() => {
        const iframe = iframeRef.current
        if (!iframe) return
        const onLoad = () => measure()
        iframe.addEventListener('load', onLoad)
        return () => iframe.removeEventListener('load', onLoad)
    }, [])

    return (
        <div
            className={cn('relative overflow-hidden', className)}
            style={{ height: `${contentHeight * scale}px` }}
        >
            <iframe
                ref={iframeRef}
                title={title}
                sandbox="allow-same-origin"
                srcDoc={doc}
                style={{
                    width: `${EMAIL_WIDTH}px`,
                    height: `${contentHeight}px`,
                    border: 0,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                }}
            />
        </div>
    )
}
